import {useEffect,useMemo,useState} from "react";
import {BellRing,MonitorSmartphone,SlidersHorizontal,X} from "lucide-react";
import {api,type NotificationType} from "./api";
import {disableWindowsNotifications,enableWindowsNotifications,windowsNotificationState,type WindowsNotificationState} from "./windows-notifications";

const windowsStateText:Record<WindowsNotificationState,string>={
  unsupported:"Este navegador não permite notificações do Windows.",
  blocked:"Bloqueadas pelo navegador. Libere em Configurações do site (cadeado na barra de endereço).",
  inactive:"Receba os avisos no canto da tela, mesmo com o SGQ fechado.",
  active:"Ativas neste navegador.",
};

/**
 * Notificações do Windows deste navegador (ativar, testar, desativar) e a escolha pessoal de quais
 * avisos chegam ao Windows. Fica no card de Notificações das Configurações.
 */
export function WindowsNotificationSettings({onError}:{onError:(message:string)=>void}){
  const [choosing,setChoosing]=useState(false);
  return <>
    <WindowsNotificationsPanel onError={onError} onChoose={()=>setChoosing(true)}/>
    {choosing&&<WindowsNotificationPreferencesDialog onError={onError} onClose={()=>setChoosing(false)}/>}
  </>;
}

function WindowsNotificationsPanel({onError,onChoose}:{onError:(message:string)=>void;onChoose:()=>void}){
  const [state,setState]=useState<WindowsNotificationState>(),[publicKey,setPublicKey]=useState<string|null>(),[busy,setBusy]=useState(false),[feedback,setFeedback]=useState("");
  useEffect(()=>{
    let active=true;
    Promise.all([api.windowsNotificationConfig(),windowsNotificationState()])
      .then(([config,current])=>{if(active){setPublicKey(config.publicKey);setState(current)}})
      .catch(()=>{if(active){setPublicKey(null);setState("unsupported")}});
    return()=>{active=false};
  },[]);
  const run=async(action:()=>Promise<void>)=>{setBusy(true);setFeedback("");try{await action()}catch(error){onError((error as Error).message)}finally{setBusy(false)}};
  const enable=()=>run(async()=>{if(!publicKey)return;const next=await enableWindowsNotifications(publicKey);setState(next);if(next==="active")setFeedback("Ativadas. Use “Testar” para ver uma notificação.")});
  const disable=()=>run(async()=>{setState(await disableWindowsNotifications());setFeedback("Desativadas neste navegador.")});
  const test=()=>run(async()=>{const result=await api.testWindowsNotification();setFeedback(result.sent>0?"Teste enviado. Ele aparece no canto da tela.":"Nenhum navegador ativo recebeu o teste. Desative e ative de novo.")});
  if(state===undefined||publicKey===undefined)return null;
  const configured=Boolean(publicKey);
  return <section className={`notification-windows${state==="active"?" is-active":""}`} aria-label="Notificações do Windows">
    <span className="notification-windows-icon">{state==="active"?<BellRing/>:<MonitorSmartphone/>}</span>
    <div>
      <strong>Notificações do Windows</strong>
      <small>{configured?windowsStateText[state]:"Ainda não configuradas no servidor do SGQ."}</small>
      {feedback&&<small className="notification-windows-feedback" role="status">{feedback}</small>}
      {configured&&state!=="unsupported"&&<button type="button" className="notification-windows-link" onClick={onChoose}><SlidersHorizontal/>Escolher quais avisos receber</button>}
    </div>
    {configured&&state==="inactive"&&<button type="button" className="button primary" disabled={busy} onClick={()=>void enable()}>Ativar</button>}
    {configured&&state==="active"&&<div className="notification-windows-actions">
      <button type="button" className="button secondary" disabled={busy} onClick={()=>void test()}>Testar</button>
      <button type="button" className="button secondary" disabled={busy} onClick={()=>void disable()}>Desativar</button>
    </div>}
  </section>;
}

/** Escolha pessoal dos tipos de aviso que vão para o Windows, como "Minhas notificações" do Portal de Chamados. */
function WindowsNotificationPreferencesDialog({onError,onClose}:{onError:(message:string)=>void;onClose:()=>void}){
  const [types,setTypes]=useState<NotificationType[]>(),[disabled,setDisabled]=useState<Set<string>>(new Set()),[saving,setSaving]=useState(false);
  useEffect(()=>{
    let active=true;
    Promise.all([api.notificationTypes(),api.windowsNotificationPreferences()])
      .then(([list,preferences])=>{if(active){setTypes(list);setDisabled(new Set(preferences.disabled))}})
      .catch(error=>{if(active){onError((error as Error).message);onClose()}});
    return()=>{active=false};
  },[]);
  useEffect(()=>{const close=(event:KeyboardEvent)=>{if(event.key==="Escape"&&!saving)onClose()};document.addEventListener("keydown",close);return()=>document.removeEventListener("keydown",close)},[saving]);
  const groups=useMemo(()=>{
    const map=new Map<string,NotificationType[]>();
    for(const type of types||[])map.set(type.category,[...(map.get(type.category)||[]),type]);
    return [...map.entries()];
  },[types]);
  const total=types?.length||0,enabledCount=(types||[]).filter(type=>!disabled.has(type.id)).length;
  const setMany=(ids:string[],enabled:boolean)=>setDisabled(current=>{const next=new Set(current);for(const id of ids){if(enabled)next.delete(id);else next.add(id)}return next});
  const save=async()=>{
    setSaving(true);
    try{await api.saveWindowsNotificationPreferences([...disabled]);onClose()}
    catch(error){onError((error as Error).message)}
    finally{setSaving(false)}
  };
  return <div className="dialog-backdrop" role="presentation" onMouseDown={event=>{if(event.target===event.currentTarget&&!saving)onClose()}}>
    <section className="module-dialog notification-preferences" role="dialog" aria-modal="true" aria-labelledby="notification-preferences-title">
      <header>
        <div><h2 id="notification-preferences-title">Avisos no Windows</h2><p>Escolha quais avisos do sino também aparecem no canto da tela. A escolha vale para todos os seus navegadores.</p></div>
        <button type="button" className="icon-button" aria-label="Fechar" disabled={saving} onClick={onClose}><X/></button>
      </header>
      {!types?<p className="notification-empty">Carregando tipos de aviso…</p>:<>
        <div className="notification-preferences-toolbar">
          <span>{enabledCount===total?"Recebendo todos os avisos":`${enabledCount} de ${total} avisos ativos`}</span>
          <div>
            <button type="button" className="button secondary" disabled={saving||enabledCount===total} onClick={()=>setMany(types.map(type=>type.id),true)}>Marcar todos</button>
            <button type="button" className="button secondary" disabled={saving||enabledCount===0} onClick={()=>setMany(types.map(type=>type.id),false)}>Desmarcar todos</button>
          </div>
        </div>
        <div className="notification-preferences-groups">
          {groups.map(([category,items])=>{
            const on=items.filter(item=>!disabled.has(item.id)).length;
            return <fieldset key={category} disabled={saving}>
              <legend>
                <label><input type="checkbox" checked={on===items.length} ref={input=>{if(input)input.indeterminate=on>0&&on<items.length}} onChange={event=>setMany(items.map(item=>item.id),event.target.checked)}/><strong>{category}</strong></label>
                <small>{on} de {items.length}</small>
              </legend>
              {items.map(item=><label key={item.id} className="notification-preferences-item">
                <input type="checkbox" checked={!disabled.has(item.id)} onChange={event=>setMany([item.id],event.target.checked)}/>
                <span><strong>{item.label}</strong><small>{item.description}</small></span>
              </label>)}
            </fieldset>;
          })}
        </div>
        <footer className="notification-preferences-footer">
          <button type="button" className="button secondary" disabled={saving} onClick={onClose}>Cancelar</button>
          <button type="button" className="button primary" disabled={saving} onClick={()=>void save()}>{saving?"Salvando…":"Salvar preferências"}</button>
        </footer>
      </>}
    </section>
  </div>;
}
